import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { VanillaGalleryEngine } from '../lib/VanillaGalleryEngine';

// Enhanced memory pressure detection with multiple indicators
function detectMemoryPressure() {
  const status = {
    pressure: false,
    level: 'low', // low, medium, high, critical
    usedMB: 0,
    limitMB: 0,
    usage: 0,
    reasons: []
  };

  try {
    // Primary: Performance API memory info
    if (performance.memory) {
      const used = performance.memory.usedJSHeapSize;
      const limit = performance.memory.jsHeapSizeLimit;
      const usage = used / limit;
      
      status.usedMB = Math.round(used / 1048576);
      status.limitMB = Math.round(limit / 1048576);
      status.usage = usage;

      if (usage > 0.9) {
        status.pressure = true;
        status.level = 'critical';
        status.reasons.push('Memory usage > 90%');
      } else if (usage > 0.8) {
        status.pressure = true;
        status.level = 'high';
        status.reasons.push('Memory usage > 80%');
      } else if (usage > 0.65) {
        status.pressure = true;
        status.level = 'medium';
        status.reasons.push('Memory usage > 65%');
      }
    }

    // Secondary: Device memory constraints
    if (navigator.deviceMemory) {
      if (navigator.deviceMemory <= 2) {
        status.pressure = true;
        status.level = 'high';
        status.reasons.push(`Low device memory: ${navigator.deviceMemory}GB`);
      } else if (navigator.deviceMemory <= 4) {
        status.pressure = true;
        status.level = 'medium';
        status.reasons.push(`Limited device memory: ${navigator.deviceMemory}GB`);
      }
    }

    // Tertiary: Mobile device detection (often memory-constrained)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      status.pressure = true;
      status.level = status.level === 'low' ? 'medium' : status.level;
      status.reasons.push('Mobile device detected');
    }

    // Log memory status for debugging
    if (status.pressure && status.reasons.length > 0) {
      console.log(`⚠️  Memory pressure detected: ${status.level.toUpperCase()} (${status.usedMB}MB/${status.limitMB}MB - ${(status.usage * 100).toFixed(1)}%)`);
      console.log(`   Reasons: ${status.reasons.join(', ')}`);
    }

    return status;
  } catch (error) {
    console.warn('Memory pressure detection failed:', error);
    return status; // Return default low-pressure status
  }
}

// Proactive cleanup functions for memory pressure
async function performEnhancedCleanup() {
  try {
    // Clear THREE.js caches
    if (THREE.Cache && THREE.Cache.files) {
      THREE.Cache.clear();
    }
    
    // Clear texture and material caches
    if (THREE.ImageUtils) {
      THREE.ImageUtils.clearCache && THREE.ImageUtils.clearCache();
    }
    
    // Clear WebGL state
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
      if (gl && gl.getExtension) {
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) {
          // Don't actually lose context, just clear unused resources
          console.log('🧹 Enhanced cleanup: WebGL resources cleared');
        }
      }
    }
    
    // Suggest garbage collection
    if (window.gc) {
      window.gc();
    }
    
    console.log('✅ Enhanced cleanup completed');
  } catch (error) {
    console.warn('Enhanced cleanup failed:', error);
  }
}

async function performAggressiveCleanup() {
  try {
    // Start with enhanced cleanup
    await performEnhancedCleanup();
    
    // Additional aggressive measures
    console.log('💀 Starting aggressive cleanup...');
    
    // Clear any remaining caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          if (cacheName.includes('texture') || cacheName.includes('model')) {
            return caches.delete(cacheName);
          }
        })
      );
    }
    
    // Clear URL object caches
    const urlObjects = document.querySelectorAll('[src^="blob:"]');
    urlObjects.forEach(element => {
      if (element.src) {
        URL.revokeObjectURL(element.src);
      }
    });
    
    // Force multiple GC cycles for critical situations
    if (window.gc) {
      for (let i = 0; i < 3; i++) {
        window.gc();
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log('💀 Aggressive cleanup completed');
  } catch (error) {
    console.warn('Aggressive cleanup failed:', error);
  }
}

// Detect incognito/private browsing mode
async function detectIncognitoMode() {
  try {
    // Method 1: Storage quota (most reliable)
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      // Incognito typically has much lower quota (< 100MB vs several GB)
      return estimate.quota < 120000000; // Less than ~120MB suggests incognito
    }
    
    // Method 2: IndexedDB detection (fallback)
    return new Promise((resolve) => {
      const db = indexedDB.open('test');
      db.onerror = () => resolve(true); // Likely incognito if fails
      db.onsuccess = () => {
        resolve(false);
        indexedDB.deleteDatabase('test');
      };
    });
  } catch (e) {
    // If detection fails, assume worst case (incognito)
    return true;
  }
}

// Wait for actual cleanup completion instead of fixed delays
async function waitForCleanupCompletion(isIncognito, isMemoryConstrained, memoryPressure) {
  // Cleanup in progress
  
  const startTime = performance.now();
  const maxWaitTime = isIncognito ? 5000 : 2000; // Max 5s for incognito, 2s for regular
  
  // Check memory pressure if available
  const initialMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
  
  // Polling function to check if cleanup is actually complete
  const checkCleanupComplete = async () => {
    // Force GC if available and memory-constrained
    if ((isMemoryConstrained || memoryPressure) && window.gc) {
      window.gc();
    }
    
    // Check if memory has decreased (indicating cleanup progress)
    if (performance.memory) {
      const currentMemory = performance.memory.usedJSHeapSize;
      const memoryReduced = currentMemory < (initialMemory * 0.95); // 5% reduction
      
      if (memoryReduced) {
        // Memory cleanup complete
        return true;
      }
    }
    
    // Check THREE.js cache state
    const cacheSize = Object.keys(THREE.Cache.files).length;
    if (cacheSize === 0) {
      // Cache cleanup complete
      return true;
    }
    
    // Time-based fallback
    const elapsed = performance.now() - startTime;
    if (elapsed > maxWaitTime) {
      console.log(`⏰ Cleanup timeout after ${elapsed.toFixed(0)}ms - proceeding anyway`);
      return true;
    }
    
    return false;
  };
  
  // Poll until cleanup is complete
  while (!(await checkCleanupComplete())) {
    await new Promise(resolve => setTimeout(resolve, 50)); // Check every 50ms
  }
  
  const totalTime = performance.now() - startTime;
  // Cleanup completed
}

// Wait for artwork loading to complete with memory stability check
async function waitForArtworkLoadComplete(loadFunction) {
  const startTime = performance.now();
  const maxWaitTime = 10000; // 10 second max per artwork
  
  const initialMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
  
  // Execute the loading function
  await loadFunction();
  
  // Wait for memory to stabilize after loading
  let memoryStableCount = 0;
  let lastMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
  
  const checkLoadingComplete = async () => {
    if (performance.memory) {
      const currentMemory = performance.memory.usedJSHeapSize;
      const memoryDelta = Math.abs(currentMemory - lastMemory);
      const memoryStable = memoryDelta < 1048576; // Less than 1MB change
      
      if (memoryStable) {
        memoryStableCount++;
        if (memoryStableCount >= 3) { // 3 consecutive stable readings (150ms)
          console.log(`✅ Artwork load complete - memory stable at ${(currentMemory/1048576).toFixed(1)}MB`);
          return true;
        }
      } else {
        memoryStableCount = 0; // Reset if memory is still changing
      }
      
      lastMemory = currentMemory;
    }
    
    // Time-based fallback
    const elapsed = performance.now() - startTime;
    if (elapsed > maxWaitTime) {
      console.log(`⏰ Artwork load timeout after ${elapsed.toFixed(0)}ms`);
      return true;
    }
    
    return false;
  };
  
  // Poll until loading is complete
  while (!(await checkLoadingComplete())) {
    await new Promise(resolve => setTimeout(resolve, 50)); // Check every 50ms
  }
  
  // Final GC for incognito mode
  if (window.gc) window.gc();
  
  const totalTime = performance.now() - startTime;
  console.log(`🎯 Artwork loading completed in ${totalTime.toFixed(0)}ms`);
}

export const useGalleryEngine = (loadingManager) => {
  const canvasRef = useRef();
  const engineRef = useRef();
  const [engineReady, setEngineReady] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [backgroundLoadingProgress, setBackgroundLoadingProgress] = useState({ progress: 0, loaded: 0, total: 0 });
  const [roomReadiness, setRoomReadiness] = useState(new Map()); // Track which rooms are ready

  // Initialize Three.js engine
  useEffect(() => {
    // Use setTimeout to ensure DOM is ready
    const timer = setTimeout(() => {
      if (canvasRef.current && !engineRef.current) {
        try {
          engineRef.current = new VanillaGalleryEngine(canvasRef.current, loadingManager);
          setEngineReady(true);
        } catch (error) {
          console.error('Failed to initialize Three.js engine:', error);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
    };
  }, []);

  const updateScene = useCallback(async (currentShowroom, placements, items, currentShowroomId, setSceneLoading, allShowrooms = null) => {
    if (!engineRef.current || !currentShowroom) {
      return;
    }

    try {
      // CRITICAL: Check if we can do instant room switching instead of rebuilding
      if (engineRef.current.isRoomReady && engineRef.current.isRoomReady(currentShowroomId)) {
        console.log(`⚡ INSTANT SWITCH to room ${currentShowroomId}`);
        
        const success = engineRef.current.switchToRoom(currentShowroomId, placements, items);
        if (success) {
          setSceneReady(true);
          if (setSceneLoading) setSceneLoading(false);
          return; // Skip the entire rebuild process!
        }
      }
      
      // Start scene loading
      if (setSceneLoading) setSceneLoading(true);
      setSceneReady(false);

      // Clear existing scene and texture cache - BUT NOT in multi-room mode!
      if (!engineRef.current.multiRoomEnabled) {
        engineRef.current.clearRoom();
      }
      
      // Detect device capabilities and memory pressure
      const isIncognito = await detectIncognitoMode();
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
      const isMemoryConstrained = isIncognito || isLowMemory || isMobile;
      
      // Enhanced memory pressure detection
      const memoryStatus = detectMemoryPressure();
      const memoryPressure = memoryStatus.pressure;
      
      // Proactive memory management based on pressure level
      if (memoryStatus.level === 'critical') {
        console.log('💥 CRITICAL memory pressure - applying aggressive cleanup');
        await performAggressiveCleanup();
        // Force garbage collection if available
        if (window.gc) {
          window.gc();
          // Wait a moment for GC to complete
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } else if (memoryStatus.level === 'high') {
        console.log('🔥 HIGH memory pressure - applying enhanced cleanup');
        await performEnhancedCleanup();
      }
      
      // Scene verification
      
      // Wait for cleanup completion instead of fixed delays
      await waitForCleanupCompletion(isIncognito, isMemoryConstrained, memoryPressure);

      // Create room (this may take time for custom models)
      // Creating room
      
      try {
        const roomLoadSuccess = await engineRef.current.createRoom(currentShowroom);
        
        if (!roomLoadSuccess) {
          console.error('❌ Room creation failed - validation error');
          throw new Error('Room failed validation after creation');
        }
      } catch (error) {
        console.error('❌ Room creation failed:', error.message);
        
        // For custom rooms, show specific error messages
        if (currentShowroom.modelType === 'custom') {
          if (error.message.includes('memory')) {
            console.error('💥 Memory error for custom room');
            // Don't proceed - show error to user
            if (setSceneLoading) setSceneLoading(false);
            return;
          } else {
            console.error('🌐 Network/file error for custom room');
            // Don't proceed - show error to user  
            if (setSceneLoading) setSceneLoading(false);
            return;
          }
        } else {
          // For procedural rooms, this should never happen, but handle gracefully
          console.error('❌ Procedural room creation failed - this is unexpected');
          if (setSceneLoading) setSceneLoading(false);
          return;
        }
      }
      
      console.log('✅ Room created and validated successfully');
      
      // For custom models, wait for async operations to stabilize
      if (currentShowroom.modelType === 'custom' && currentShowroom.modelUrl) {
        console.log('⏳ Custom room detected - waiting for 3D model to fully stabilize...');
        await new Promise(resolve => setTimeout(resolve, 1500)); // Wait for meshes to be processed
      }

      // Add artworks in controlled batches (sequential for incognito)
      const roomPlacements = placements.filter(p => p.showroomId === currentShowroomId);
      console.log(`🖼️ Adding ${roomPlacements.length} artworks...`);
      
      if (isIncognito) {
        console.log('🕵️ Incognito mode - loading artworks sequentially with completion tracking');
        // Sequential loading for incognito mode with proper completion tracking
        for (let i = 0; i < roomPlacements.length; i++) {
          const placement = roomPlacements[i];
          const item = items.find(itm => itm.id === placement.itemId);
          if (item) {
            // Loading artwork sequentially
            
            // Track loading completion instead of using fixed delays
            await waitForArtworkLoadComplete(() => engineRef.current.addArtwork(item, placement));
          }
        }
      } else {
        // Batch loading for regular mode (existing behavior)
        const batchSize = 3;
        for (let i = 0; i < roomPlacements.length; i += batchSize) {
          const batch = roomPlacements.slice(i, i + batchSize);
          
          await Promise.all(batch.map(async (placement, batchIndex) => {
            const item = items.find(itm => itm.id === placement.itemId);
            if (item) {
              // Loading artwork in batch
              await engineRef.current.addArtwork(item, placement);
            }
          }));
          
          // Delay between batches to prevent overwhelming texture loader
          if (i + batchSize < roomPlacements.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      
      // Extra time for texture loading to stabilize BEFORE camera positioning
      console.log('⏳ Waiting for textures to load...');
      const finalWaitTime = currentShowroom.modelType === 'custom' && currentShowroom.modelUrl ? 3000 : 1000;
      console.log(`⏳ Final stabilization wait: ${finalWaitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, finalWaitTime));
      
      // Position camera at safe room center AFTER everything is fully loaded
      engineRef.current.positionCameraAtRoomCenter(roomPlacements, items);
      
      // Final validation before showing scene
      const finalSceneCount = engineRef.current.scene?.children?.length || 0;
      console.log(`🔍 Final scene validation: ${finalSceneCount} objects in scene`);
      
      if (finalSceneCount === 0) {
        console.error('❌ Scene appears empty after loading - extending wait time');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Extra wait for empty scenes
      }
      
      // Log final memory usage
      if (performance.memory) {
        console.log(`💾 Memory after loading - Used: ${(performance.memory.usedJSHeapSize / 1048576).toFixed(1)}MB, Limit: ${(performance.memory.jsHeapSizeLimit / 1048576).toFixed(1)}MB`);
      }
      
      // Scene is ready
      // Scene ready
      setSceneReady(true);
      if (setSceneLoading) setSceneLoading(false);
      
      // Start background loading of other rooms after first room is ready
      if (engineRef.current.startBackgroundLoading && allShowrooms && allShowrooms.length > 1) {
        console.log(`🚀 BACKGROUND LOADING STARTED`);
        
        // Start background loading with intelligent throttling
        try {
          engineRef.current.startBackgroundLoading(allShowrooms, items, placements, currentShowroomId);
          // Background loading initiated
          
          // Start monitoring background loading progress
          startProgressMonitoring(allShowrooms.length);
        } catch (error) {
          console.error('❌ Failed to start background loading:', error);
        }
      }
    } catch (error) {
      console.error('❌ Error updating scene:', error);
      if (setSceneLoading) setSceneLoading(false);
    }
  }, []);

  // Progress monitoring for background loading
  const startProgressMonitoring = useCallback((totalRooms) => {
    // Starting progress monitoring
    
    const monitorInterval = setInterval(() => {
      if (engineRef.current && engineRef.current.getLoadingProgress) {
        const progress = engineRef.current.getLoadingProgress();
        
        // Update progress state
        setBackgroundLoadingProgress(progress);
        
        // Update room readiness state
        if (engineRef.current.multiRoomManager) {
          const roomStates = engineRef.current.multiRoomManager.getAllRoomStates();
          setRoomReadiness(new Map(roomStates));
        }
        
        // Stop monitoring when complete
        if (!progress.isActive && progress.loaded === progress.total) {
          console.log('✅ Background loading completed - all rooms ready for instant switching');
          clearInterval(monitorInterval);
        }
      }
    }, 1000); // Update every second
    
    // Cleanup after 10 minutes max
    setTimeout(() => {
      clearInterval(monitorInterval);
    }, 600000);
  }, []);

  const setOnInteraction = useCallback((callback) => {
    if (engineRef.current && engineReady) {
      engineRef.current.setOnInteraction(callback);
    }
  }, [engineReady]);

  return {
    canvasRef,
    engineRef,
    engineReady,
    sceneReady,
    setSceneReady,
    updateScene,
    setOnInteraction,
    backgroundLoadingProgress,
    roomReadiness
  };
};